import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { gbp, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import OnboardingCard from "@/components/OnboardingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import DatePicker, { parseDate, fmtDate } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, RefreshCw, Save, Plus, Building2, ChevronLeft, ChevronRight, Trash2, Archive, Users, Briefcase, History, Receipt, Handshake, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  draft: "secondary", sent: "default", viewed: "default", accepted: "default",
  rejected: "destructive", superseded: "outline", withdrawn: "outline", expired: "outline",
};

const NAME_TITLES = new Set(["mr", "mrs", "ms", "miss", "dr", "sir", "mx", "prof", "the"]);
// Mirrors backend server._name_sig: title/case/middle-name/punctuation-insensitive first|last signature.
const nameSig = (name) => {
  const raw = String(name || "");
  const surnameFirst = raw.includes(",");
  const toks = raw.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter((t) => t && !NAME_TITLES.has(t));
  if (!toks.length) return "";
  if (toks.length === 1) return toks[0];
  const [first, last] = surnameFirst ? [toks[toks.length - 1], toks[0]] : [toks[0], toks[toks.length - 1]];
  return `${first}|${last}`;
};
// Number of duplicate contacts that a merge would remove.
const duplicateCount = (contacts) => {
  const seen = new Set();
  let dupes = 0;
  for (const ct of contacts || []) {
    const sig = nameSig(ct?.name) || String(ct?.name || "");
    if (seen.has(sig)) dupes += 1; else seen.add(sig);
  }
  return dupes;
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", job_title: "", phone: "", signing_authority: false });
  const [chConfigured, setChConfigured] = useState(false);
  const [chNum, setChNum] = useState("");
  const [chBusy, setChBusy] = useState(false);
  const [chDirectorsOnly, setChDirectorsOnly] = useState(true);
  const [ssCat, setSsCat] = useState(null);
  const [ssOptions, setSsOptions] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [xeroInv, setXeroInv] = useState(null);
  const [showAllProps, setShowAllProps] = useState(false);
  const [showAllXero, setShowAllXero] = useState(false);
  useEffect(() => { api.get(`/xero/client-invoices/${id}`).then((r) => setXeroInv(r.data)).catch(() => {}); }, [id]);

  const load = () => api.get(`/clients/${id}`).then((r) => { setC(r.data); setChNum(r.data.company_number || ""); });
  useEffect(() => {
    load();
    api.get("/companies-house/status").then((r) => setChConfigured(r.data.configured)).catch(() => {});
    api.get("/xero/status").then((r) => {
      const cat = r.data.sales_source_category;
      setSsCat(cat || null);
      if (cat) api.get("/xero/tracking-categories").then((t) => {
        const found = (t.data || []).find((x) => x.name === cat);
        setSsOptions(found ? found.options : []);
      }).catch(() => {});
    }).catch(() => {});
  }, [id]);

  // Ordered sibling list (matches the Clients list order) for prev/next navigation.
  useEffect(() => {
    api.get("/clients").then((r) => {
      const sorted = [...r.data].sort((a, b) =>
        (a.trading_name || a.legal_name || "").localeCompare(b.trading_name || b.legal_name || "", undefined, { sensitivity: "base" }));
      setSiblings(sorted.map((x) => ({ id: x.id, name: x.trading_name || x.legal_name })));
    }).catch(() => {});
  }, []);

  const curIdx = siblings.findIndex((s) => s.id === id);
  const prevClient = curIdx > 0 ? siblings[curIdx - 1] : null;
  const nextClient = curIdx >= 0 && curIdx < siblings.length - 1 ? siblings[curIdx + 1] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName || "";
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft" && prevClient) navigate(`/clients/${prevClient.id}`);
      if (e.key === "ArrowRight" && nextClient) navigate(`/clients/${nextClient.id}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevClient, nextClient, navigate]);

  const chSync = async () => {
    if (!chNum) return toast.error("Enter a company number first");
    setChBusy(true);
    try {
      const { data } = await api.put(`/clients/${id}`, { company_number: chNum });
      const res = await api.post(`/clients/${id}/companies-house/sync`, { company_number: chNum, import_officers: true });
      setC({ ...res.data, proposals: c.proposals });
      toast.success("Synced from Companies House — details, dates & directors updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setChBusy(false); }
  };

  if (!c) return <div className="p-8 text-cass-slate">Loading…</div>;

  const curPropId = c.most_recent_accepted_proposal || (c.proposals || []).find((p) => p.status === "accepted")?.id;
  const curPropRef = c.current_proposal_ref || (c.proposals || []).find((p) => p.id === curPropId)?.reference;

  const archiveClient = async () => {
    const next = !c.archived;
    if (next && !window.confirm("Archive this record? It will be hidden from the active clients list but can be restored anytime.")) return;
    try { await api.post(`/clients/${id}/archive`, { archived: next }); setC({ ...c, archived: next }); toast.success(next ? "Record archived" : "Record restored"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const deleteClient = async () => {
    if (!window.confirm("Permanently delete this record? This cannot be undone.")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Record deleted"); navigate("/clients"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const save = async () => {
    const { proposals, ...body } = c;
    try { await api.put(`/clients/${id}`, body); toast.success("Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const addContact = async () => {
    if (!contact.name || !contact.email) return toast.error("Name and email required");
    const contacts = [...(c.contacts || []), contact];
    await api.put(`/clients/${id}`, { contacts });
    setContact({ name: "", email: "", job_title: "", phone: "", signing_authority: false });
    load();
    toast.success("Contact added");
  };

  const removeContact = async (i) => {
    const contacts = c.contacts.filter((_, j) => j !== i);
    setC({ ...c, contacts }); await saveContacts(contacts);
  };
  const dedupeContacts = async () => {
    const { data } = await api.post(`/clients/${id}/contacts/dedupe`);
    toast.success(data.removed ? `Merged ${data.removed} duplicate contact(s)` : "No duplicate contacts found");
    load();
  };
  const updateContact = (i, patch) => setC({ ...c, contacts: c.contacts.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const saveContacts = async (list) => { await api.put(`/clients/${id}`, { contacts: list || c.contacts }); toast.success("Contacts saved"); };
  const toggleSignatory = async (i) => {
    const contacts = c.contacts.map((x, j) => (j === i ? { ...x, signing_authority: !x.signing_authority } : x));
    setC({ ...c, contacts }); await saveContacts(contacts);
  };
  const ackChanges = async () => { await api.post(`/clients/${id}/ch-acknowledge`); setC({ ...c, ch_change_flag: false }); toast.success("Acknowledged"); };

  const accepted = (c.proposals || []).filter((p) => p.status === "accepted" && p.proposal_type === "new");
  const primaryContact = (c.contacts || []).find((x) => x.signing_authority) || (c.contacts || [])[0];
  const derivedFirst = (primaryContact?.name || "").trim().split(/\s+/)[0] || "";

  const startRenewal = () => {
    const src = (c.proposals || []).find((p) => p.status === "accepted");
    if (!src) return toast.error("No accepted proposal to renew");
    navigate(`/proposals/new?type=renewal&client=${id}&renewalOf=${src.id}`);
  };

  const expiryFromStart = (s) => { const d = parseDate(s); if (!d) return ""; const e = new Date(d); e.setFullYear(e.getFullYear() + 1); e.setDate(e.getDate() - 1); return fmtDate(e); };
  const renewalFromExpiry = (x) => { const d = parseDate(x); if (!d) return ""; const r = new Date(d); r.setDate(r.getDate() - 30); return fmtDate(r); };
  const setCpStart = (v) => { const exp = expiryFromStart(v); setC({ ...c, agreement_start_date: v, ...(exp ? { proposal_expiry_date: exp, next_renewal_date: renewalFromExpiry(exp) } : {}) }); };
  const setCpExpiry = (v) => { setC({ ...c, proposal_expiry_date: v, ...(v ? { next_renewal_date: renewalFromExpiry(v) } : {}) }); };

  const field = (k, label, type = "text") => (
    <div>
      <Label className="text-xs">{label}</Label>
      {type === "date"
        ? <DatePicker value={c[k] || ""} onChange={(v) => setC({ ...c, [k]: v })} className="w-full h-9" testId={`field-${k}`} />
        : <Input data-testid={`field-${k}`} type={type} value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} />}
    </div>
  );

  return (
    <div>
      <PageHeader
        title={c.trading_name || c.legal_name}
        subtitle={<span><Badge variant={c.client_type === "client" ? "default" : "secondary"}>{c.client_type}</Badge></span>}
        actions={
          <>
            {siblings.length > 1 && curIdx >= 0 && (
              <div className="flex items-center gap-1 mr-2 border-r border-border pr-3" data-testid="client-nav">
                <Button variant="outline" size="icon" className="h-9 w-9" disabled={!prevClient} onClick={() => prevClient && navigate(`/clients/${prevClient.id}`)} title={prevClient ? `Back: ${prevClient.name}` : "First client"} data-testid="client-nav-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-xs text-cass-slate tabular-nums px-1 whitespace-nowrap" data-testid="client-nav-pos">{curIdx + 1} of {siblings.length}</span>
                <Button variant="outline" size="icon" className="h-9 w-9" disabled={!nextClient} onClick={() => nextClient && navigate(`/clients/${nextClient.id}`)} title={nextClient ? `Next: ${nextClient.name}` : "Last client"} data-testid="client-nav-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            )}
            {chConfigured && (
              <Button variant="outline" onClick={chSync} disabled={chBusy} data-testid="ch-sync-btn"><Building2 className="w-4 h-4 mr-2" />{chBusy ? "Syncing…" : "Refresh from Companies House"}</Button>
            )}
            {c.archived && <Badge variant="outline" className="text-amber-700 border-amber-300 self-center" data-testid="archived-badge">Archived</Badge>}
            <Button variant="outline" onClick={archiveClient} data-testid="archive-client-btn"><Archive className="w-4 h-4 mr-2" />{c.archived ? "Restore" : "Archive"}</Button>
            {c.archived && user?.role === "super_admin" && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={deleteClient} data-testid="delete-client-btn"><Trash2 className="w-4 h-4 mr-2" />Delete</Button>}
            <Button variant="outline" onClick={startRenewal} data-testid="start-renewal-btn"><RefreshCw className="w-4 h-4 mr-2" />Create Renewal</Button>
            <Button onClick={() => navigate(`/proposals/new?client=${id}`)} className="bg-cass-navy text-white" data-testid="new-proposal-for-client"><Plus className="w-4 h-4 mr-2" />New Proposal</Button>
          </>
        }
      />
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-[1800px] mx-auto space-y-6">
        {c.ch_change_flag && (
          <Card className="p-4 rounded-md border-cass-bronze bg-amber-50" data-testid="ch-change-alert">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-amber-900">Companies House changes detected: {(c.ch_changes || []).slice(-3).map((x) => `${x.field} ${x.old}→${x.new}`).join("; ")}</div>
              <Button size="sm" variant="outline" onClick={ackChanges} data-testid="ack-ch-btn">Acknowledge</Button>
            </div>
          </Card>
        )}

        {/* Row 1 — business information + commercial summary sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
          <Card className="p-6 rounded-2xl border-sky-100 bg-sky-50/50 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-sky-100 text-sky-500"><Briefcase className="w-4 h-4" /></span>Business information</h3>
              <Button size="sm" onClick={save} data-testid="save-client-detail"><Save className="w-4 h-4 mr-2" />Save</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field("legal_name", "Legal entity name")}
              {field("trading_name", "Trading name")}
              <div>
                <Label className="text-xs">Preferred name</Label>
                <Input value={c.preferred_name || ""} placeholder={derivedFirst || "e.g. first name"} onChange={(e) => setC({ ...c, preferred_name: e.target.value })} data-testid="field-preferred_name" />
                <p className="text-[11px] text-cass-slate mt-1">Used in greetings. Defaults to the primary contact's first name{derivedFirst ? ` (${derivedFirst})` : ""} if left blank.</p>
              </div>
              {field("company_number", "Company number")}
              {field("website", "Website")}
              {field("industry", "Industry / sector")}
              {field("year_end", "Year-end date")}
              {field("cs01_date", "CS01 confirmation date", "date")}
              {field("vat_number", "VAT number")}
              {field("phone", "Phone")}
              {field("registered_office", "Registered office")}
              {field("current_accountant", "Current accountant")}
              {field("current_monthly_fee", "Current monthly fee (£)", "number")}
            </div>
            {ssCat && (
              <div className="mt-4 max-w-xs" data-testid="client-sales-source">
                <Label className="text-xs">Xero Sales Source ({ssCat})</Label>
                <Select value={c.xero_sales_source || "__none__"} onValueChange={(v) => setC({ ...c, xero_sales_source: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="sales-source-select"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not set</SelectItem>
                    {(c.xero_sales_source && !ssOptions.includes(c.xero_sales_source) ? [c.xero_sales_source, ...ssOptions] : ssOptions).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-cass-slate mt-1">Applied as a tracking option on this client's Xero invoices. Remember to Save.</p>
              </div>
            )}
            <div className="mt-4">
              <Label className="text-xs">Discovery / relationship notes</Label>
              <Textarea data-testid="field-notes" value={c.discovery_notes || ""} onChange={(e) => setC({ ...c, discovery_notes: e.target.value })} />
            </div>
          </Card>
          </div>

          <div className="flex flex-col gap-6">
          <Card className="p-6 rounded-2xl border-cass-teal/30 bg-cass-teal/5 border-t-4 border-t-cass-teal" data-testid="current-proposal-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-alt font-bold text-cass-navy text-lg flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-cass-teal/15 text-cass-teal"><FileText className="w-4 h-4" /></span>Current Proposal</h3>
              <Button size="sm" onClick={save} data-testid="save-current-proposal"><Save className="w-4 h-4 mr-1" />Save</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Proposal number</Label>
                {curPropId
                  ? <button className="block text-sm font-semibold text-cass-teal hover:underline mt-1.5" onClick={() => navigate(`/proposals/${curPropId}`)} data-testid="cp-ref-link">{curPropRef || "View proposal"}</button>
                  : <Input value={c.current_proposal_ref || ""} onChange={(e) => setC({ ...c, current_proposal_ref: e.target.value })} placeholder="e.g. migrated ref" data-testid="field-current_proposal_ref" />}
              </div>
              <div><Label className="text-xs">Monthly recurring fee (£)</Label><Input type="number" value={c.agreed_monthly_fee ?? ""} onChange={(e) => setC({ ...c, agreed_monthly_fee: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-agreed_monthly_fee" /></div>
              <div><Label className="text-xs">Start date</Label><DatePicker value={c.agreement_start_date || ""} onChange={setCpStart} className="w-full h-9" testId="field-agreement_start_date" /></div>
              <div><Label className="text-xs">One-off fees (£)</Label><Input type="number" value={c.current_oneoff_fee ?? ""} onChange={(e) => setC({ ...c, current_oneoff_fee: e.target.value === "" ? null : Number(e.target.value) })} data-testid="field-current_oneoff_fee" /></div>
              <div><Label className="text-xs">Proposal expiry date</Label><DatePicker value={c.proposal_expiry_date || ""} onChange={setCpExpiry} className="w-full h-9" testId="field-proposal_expiry_date" /><p className="text-[11px] text-cass-slate mt-1">Auto: start + 1 year − 1 day</p></div>
              <div><Label className="text-xs">Renewal date</Label><DatePicker value={c.next_renewal_date || ""} onChange={(v) => setC({ ...c, next_renewal_date: v })} className="w-full h-9" testId="field-next_renewal_date" /><p className="text-[11px] text-cass-slate mt-1">Auto: 30 days before expiry · triggers renewals</p></div>
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-emerald-100 bg-emerald-50/50">
            <h3 className="font-alt font-semibold text-cass-navy mb-2 flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-emerald-100 text-emerald-500"><Handshake className="w-4 h-4" /></span>Engagement</h3>
            <div className="text-sm space-y-1 text-cass-slate">
              <div>Agreed MRR: <span className="font-semibold text-cass-navy tabular">{c.agreed_monthly_fee ? gbp(c.agreed_monthly_fee) : "—"}</span></div>
              <div>Active services: {(c.active_services || []).length}</div>
              <div>Last renewal: {c.last_renewal_date ? new Date(c.last_renewal_date).toLocaleDateString("en-GB") : "—"}</div>
            </div>
            {(c.active_services || []).length > 0 && (
              <><Separator className="my-3" />
              <div className="flex flex-wrap gap-1">{c.active_services.map((s) => <Badge key={s} variant="secondary" className="text-[11px]">{s}</Badge>)}</div></>
            )}
          </Card>

          {curPropId && <OnboardingCard proposalId={curPropId} />}

          {(c.documents || []).length > 0 && (
            <Card className="p-6 rounded-2xl" data-testid="client-documents-card">
              <h3 className="font-alt font-semibold text-cass-navy mb-3 flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-cass-teal/10 text-cass-teal"><FileText className="w-4 h-4" /></span>Documents</h3>
              <div className="space-y-2">
                {(c.documents || []).map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2" data-testid={`client-doc-${d.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-cass-navy truncate">{d.name}</div>
                      <div className="text-[11px] text-cass-slate">{d.created_by ? `${d.created_by} · ` : ""}{d.created_at ? new Date(d.created_at).toLocaleString("en-GB") : ""}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/clients/${id}/documents/${d.id}/download`, "_blank")} data-testid={`client-doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { try { await api.delete(`/clients/${id}/documents/${d.id}`); toast.success("Document removed"); load(); } catch { toast.error("Could not remove"); } }} data-testid={`client-doc-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          </div>
        </div>

        {/* Row 2 — contacts + companies house */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          <Card className="p-6 rounded-2xl border-violet-100 bg-violet-50/50 h-full">
            <h3 className="font-alt font-semibold text-cass-navy mb-1 flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-violet-100 text-violet-500"><Users className="w-4 h-4" /></span>Contacts
              {duplicateCount(c.contacts) > 0 && (
                <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-700 text-[11px] font-normal" data-testid="dupe-hint-badge">
                  {duplicateCount(c.contacts)} possible duplicate{duplicateCount(c.contacts) > 1 ? "s" : ""}
                </Badge>
              )}
            </h3>
            <p className="text-[11px] text-cass-slate mb-4" data-testid="contacts-signatory-hint">Tick <span className="font-semibold">Signatory</span> for anyone who must sign the engagement letter. Mark 2+ contacts (each with an email) to enable multiple signatories on a proposal (Any one / All must sign).</p>
            <div className="space-y-2 mb-4">
              {(c.contacts || []).map((ct, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border-b border-border pb-2" data-testid={`contact-row-${i}`}>
                  <div className="col-span-3"><div className="text-sm font-medium text-cass-navy">{ct.name}</div><div className="text-xs text-cass-slate">{ct.job_title || "—"}</div></div>
                  <Input className="col-span-3 h-8" placeholder="email@company.co.uk" value={ct.email || ""} onChange={(e) => updateContact(i, { email: e.target.value })} onBlur={() => saveContacts()} data-testid={`contact-email-${i}`} />
                  <Input className="col-span-2 h-8" placeholder="Phone" value={ct.phone || ""} onChange={(e) => updateContact(i, { phone: e.target.value })} onBlur={() => saveContacts()} data-testid={`contact-phone-${i}`} />
                  <label className="col-span-2 flex items-center gap-1 text-xs text-cass-slate cursor-pointer">
                    <input type="checkbox" checked={!!ct.signing_authority} onChange={() => toggleSignatory(i)} data-testid={`signatory-${i}`} /> Signatory
                  </label>
                  <div className="col-span-2 flex justify-end">
                    <button type="button" onClick={() => removeContact(i)} className="text-xs text-red-600 hover:underline" data-testid={`contact-remove-${i}`}>Remove</button>
                  </div>
                </div>
              ))}
              {!(c.contacts || []).length && <p className="text-sm text-cass-slate">No contacts yet.</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="contact-name" placeholder="Full name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              <Input data-testid="contact-email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              <Input placeholder="Job title" value={contact.job_title} onChange={(e) => setContact({ ...contact, job_title: e.target.value })} />
              <Input data-testid="contact-phone" placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-cass-slate">
                <input type="checkbox" checked={contact.signing_authority} onChange={(e) => setContact({ ...contact, signing_authority: e.target.checked })} /> Signing authority
              </label>
            </div>
            <Button size="sm" variant="outline" className="mt-3" onClick={addContact} data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-1" />Add contact</Button>
            {(c.contacts || []).length > 1 && <Button size="sm" variant={duplicateCount(c.contacts) > 0 ? "outline" : "ghost"} className={`mt-3 ml-2 ${duplicateCount(c.contacts) > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}`} onClick={dedupeContacts} data-testid="dedupe-contacts-btn">Merge duplicates</Button>}
          </Card>
          {chConfigured && (
            <Card className="p-6 rounded-2xl border-indigo-100 bg-indigo-50/50 h-full">
              <h3 className="font-alt font-semibold text-cass-navy mb-2 flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-indigo-100 text-indigo-500"><Building2 className="w-4 h-4" /></span>Companies House</h3>
              <p className="text-xs text-cass-slate mb-3">Attach a company number and pull the latest details, year-end, CS01 and directors.</p>
              <div className="flex gap-2">
                <Input className="h-9" placeholder="Company number" value={chNum} onChange={(e) => setChNum(e.target.value)} data-testid="ch-client-number" />
                <Button onClick={chSync} disabled={chBusy} className="bg-cass-teal text-white" data-testid="ch-client-sync-btn">{chBusy ? "…" : "Sync"}</Button>
              </div>
              <label className="flex items-center gap-2 text-xs text-cass-slate mt-2 cursor-pointer">
                <input type="checkbox" checked={chDirectorsOnly} onChange={(e) => setChDirectorsOnly(e.target.checked)} data-testid="ch-directors-only" />
                Import directors only (skip secretaries)
              </label>
              {c.ch_synced_at && (
                <div className="text-xs text-cass-slate mt-3 space-y-0.5">
                  <div>Status: <span className="text-cass-navy">{c.company_status || "—"}</span></div>
                  <div>Accounts due: {c.ch_accounts_next_due || "—"} · CS01 due: {c.ch_cs01_next_due || "—"}</div>
                  <div>Next renewal auto-set: {c.next_renewal_date ? new Date(c.next_renewal_date).toLocaleDateString("en-GB") : "—"}</div>
                  <div className="text-[11px]">Last synced {new Date(c.ch_synced_at).toLocaleString("en-GB")}</div>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Row 3 — proposal history + xero invoices */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          <Card className="p-6 rounded-2xl border-amber-100 bg-amber-50/50 h-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-amber-100 text-amber-500"><History className="w-4 h-4" /></span>Proposal history</h3>
              {(c.proposals || []).length > 3 && (
                <button onClick={() => setShowAllProps((v) => !v)} data-testid="toggle-proposal-history" className="text-xs text-cass-teal hover:underline">
                  {showAllProps ? "Show less" : `Show all ${(c.proposals || []).length}`}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(showAllProps ? (c.proposals || []) : (c.proposals || []).slice(0, 3)).map((p) => (
                <div key={p.id} data-testid={`client-proposal-${p.id}`} onClick={() => navigate(`/proposals/${p.id}`)}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-cass-bone cursor-pointer">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cass-teal" />
                    <div>
                      <div className="text-sm font-medium text-cass-navy">{p.reference}</div>
                      <div className="text-xs text-cass-slate">
                        {p.proposal_type} · {gbp(p.totals?.monthly_recurring)}/mo · <span className="font-semibold text-cass-navy">{gbp(p.totals?.annualised ?? ((p.totals?.monthly_recurring || 0) * 12))}/yr</span>
                        {p.totals?.one_off ? ` · ${gbp(p.totals.one_off)} one-off` : ""}
                      </div>
                    </div>
                  </div>
                  <Badge variant={STATUS_TONE[p.status] || "secondary"}>{p.status}</Badge>
                </div>
              ))}
              {!(c.proposals || []).length && <p className="text-sm text-cass-slate">No proposals yet.</p>}
              {!showAllProps && (c.proposals || []).length > 3 && (
                <button onClick={() => setShowAllProps(true)} data-testid="proposal-history-more" className="w-full text-center text-xs text-cass-slate hover:text-cass-teal pt-1">
                  + {(c.proposals || []).length - 3} more
                </button>
              )}
            </div>
          </Card>
          {xeroInv?.connected && (
            <Card className="p-6 rounded-2xl border-orange-100 bg-orange-50/50 h-full" data-testid="client-invoices-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><span className="w-9 h-9 rounded-lg grid place-items-center bg-orange-100 text-orange-500"><Receipt className="w-4 h-4" /></span>Xero invoices</h3>
                {((xeroInv.repeating || []).length + (xeroInv.invoices || []).length) > 3 && (
                  <button onClick={() => setShowAllXero((v) => !v)} data-testid="toggle-xero-invoices" className="text-xs text-cass-teal hover:underline">
                    {showAllXero ? "Show less" : `Show all ${(xeroInv.repeating || []).length + (xeroInv.invoices || []).length}`}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(xeroInv.repeating || []).slice(0, showAllXero ? undefined : 3).map((r) => (
                  <a key={r.id} href={`https://go.xero.com/RepeatingInvoices/Edit.aspx?invoiceID=${r.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between p-2 rounded-md bg-cass-bone/50 hover:bg-cass-bone" data-testid={`client-rep-${r.id}`}>
                    <div className="text-sm text-cass-navy">Repeating · {r.reference || "—"} <span className="text-xs text-cass-slate">({r.unit})</span></div>
                    <div className="flex items-center gap-2"><span className="text-sm text-cass-navy">{gbp(r.total)}</span><Badge variant="outline">{r.status}</Badge></div>
                  </a>
                ))}
                {(xeroInv.invoices || []).slice(0, showAllXero ? undefined : Math.max(0, 3 - Math.min((xeroInv.repeating || []).length, 3))).map((i) => (
                  <a key={i.id} href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${i.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between p-2 rounded-md hover:bg-cass-bone" data-testid={`client-inv-${i.id}`}>
                    <div><div className="text-sm text-cass-navy">{i.number || i.reference || "Invoice"}</div><div className="text-xs text-cass-slate">{i.date}</div></div>
                    <div className="flex items-center gap-2"><span className="text-sm text-cass-navy">{gbp(i.total)}</span><Badge variant="outline">{i.status}</Badge></div>
                  </a>
                ))}
                {xeroInv.linked && !xeroInv.repeating.length && !xeroInv.invoices.length && (
                  <p className="text-sm text-cass-slate" data-testid="client-inv-empty">No Xero invoices yet for this client.</p>
                )}
                {!xeroInv.linked && (
                  <p className="text-sm text-cass-slate" data-testid="client-inv-unlinked">
                    This client isn’t linked to Xero yet. Sync an accepted proposal to create their Xero contact and invoices.
                  </p>
                )}
                {!showAllXero && ((xeroInv.repeating || []).length + (xeroInv.invoices || []).length) > 3 && (
                  <button onClick={() => setShowAllXero(true)} data-testid="xero-invoices-more" className="w-full text-center text-xs text-cass-slate hover:text-cass-teal pt-1">
                    + {(xeroInv.repeating || []).length + (xeroInv.invoices || []).length - 3} more
                  </button>
                )}
              </div>
            </Card>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
